import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <main className="flex min-h-screen flex-col bg-linen">
      <header className="px-5 py-6 sm:px-8"><Logo /></header>
      <div className="flex flex-1 items-center justify-center px-5 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg rounded-[2rem] border border-ink/10 bg-white p-10 text-center shadow-2xl shadow-espresso/10"
          data-testid="payment-success-page"
        >
          {status === "checking" && (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-terracotta" />
              <h1 className="mt-6 font-serif text-2xl text-ink">Conferma del pagamento…</h1>
              <p className="mt-3 text-sm text-taupe">Stiamo verificando il tuo ordine con Stripe. Un attimo.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage/15 text-sage">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h1 className="mt-6 font-serif text-3xl text-ink">Benvenuta nella tua <em className="italic text-terracotta">prima ora di libertà</em></h1>
              <p className="mt-4 text-sm leading-relaxed text-taupe">
                Pagamento confermato. Per accedere al workbook e al percorso completo,
                entra nell'area riservata con la <strong className="text-ink">stessa email usata per il pagamento</strong>.
              </p>
              <Link
                to="/area-riservata"
                data-testid="success-member-access-button"
                className="mt-8 inline-block rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-linen shadow-lg shadow-terracotta/25 transition-all hover:bg-terracotta-dark"
              >
                Accedi all'area riservata
              </Link>
            </>
          )}
          {(status === "timeout" || status === "error") && (
            <>
              <h1 className="font-serif text-2xl text-ink">Stiamo ancora confermando…</h1>
              <p className="mt-4 text-sm leading-relaxed text-taupe">
                Se hai completato il pagamento, il tuo accesso sarà attivo a breve:
                riprova ad entrare nell'area riservata con l'email usata in cassa.
              </p>
              <Link
                to="/area-riservata"
                data-testid="success-retry-access-button"
                className="mt-8 inline-block rounded-full border border-ink/15 px-8 py-4 text-sm font-semibold text-ink transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Vai all'area riservata
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </main>
  );
}
